
import { CheckoutShippingMethodLocation } from '@/types/checkout/CheckoutShippingMethodLocation';

import { 
	CHECKOUT_KEY_BUYLIST_LOCATION, 
	CHECKOUT_KEY_PAYMENT_METHOD,
	CHECKOUT_KEY_SHIPPING_ADDRESS, 
	CHECKOUT_KEY_SHIPPING_METHOD 
} from '@/constants/checkout';

import Address from '@/models/geographies/Address';
import CheckoutStep from '@/models/checkout/CheckoutStep';
import StepBuylistLocation, { StepBuylistLocationModelProps } from './steps/StepBuylistLocation';
import StepShippingAddress, { StepShippingAddressModelProps } from '@/models/checkout/steps/StepShippingAddress';
import StepShippingMethod, { StepShippingMethodModelProps } from '@/models/checkout/steps/StepShippingMethod';
import StepPaymentMethod, { StepPaymentMethodModelProps } from './steps/StepPaymentMethod';

// JS Model Props
export interface CheckoutSessionModelProps {
	stepData: Record<string, any>;
}

// API Response Props
// No API props; data stored locally only

// Constructor Props
export type CheckoutSessionCtorProps = CheckoutSessionModelProps;


// This model behaves a little differently, since it's not set by an API response
// Model sets step data directly to local Redux store
// Step data is written in useCheckout hook for use by components


export default class CheckoutSession {

	stepData: Record<string, any>;

	constructor(props: CheckoutSessionCtorProps) {
		
		const mappedProps = CheckoutSession.mapDataToModel(props);

		this.stepData = mappedProps.stepData;
	}

	// Explicitly map API response to the internal model format
	private static mapDataToModel(props: CheckoutSessionCtorProps): CheckoutSessionModelProps {
				
		return {
			stepData: props.stepData || {},
		};
	}

	// Overloads (signatures only)
	getStepData(key: typeof CHECKOUT_KEY_SHIPPING_ADDRESS): StepShippingAddress | null;
	getStepData(key: typeof CHECKOUT_KEY_SHIPPING_METHOD): StepShippingMethod | null;
	getStepData(key: typeof CHECKOUT_KEY_PAYMENT_METHOD): StepPaymentMethod | null;
	getStepData(key: typeof CHECKOUT_KEY_BUYLIST_LOCATION): StepBuylistLocation | null;
	getStepData(key: string): CheckoutStep | null;

	getStepData(key: string) {
		if(!this.stepData[key]) { return null; }

		switch(key) {
			case CHECKOUT_KEY_SHIPPING_ADDRESS:
				return new StepShippingAddress(this.stepData[key] as StepShippingAddressModelProps);
			case CHECKOUT_KEY_SHIPPING_METHOD:
				return new StepShippingMethod(this.stepData[key] as StepShippingMethodModelProps);
			case CHECKOUT_KEY_PAYMENT_METHOD:
				return new StepPaymentMethod(this.stepData[key] as StepPaymentMethodModelProps);
			case CHECKOUT_KEY_BUYLIST_LOCATION:
				return new StepBuylistLocation(this.stepData[key] as StepBuylistLocationModelProps);
			default:
				return new CheckoutStep(this.stepData[key]);
		}
	}

	get taxRateSales(): number {
		const shippingAddressStep = this.getStepData(CHECKOUT_KEY_SHIPPING_ADDRESS);
		return shippingAddressStep?.taxRateSales ?? 0;
	}

	get shippingCost(): number {
		const shippingMethodStep = this.getStepData(CHECKOUT_KEY_SHIPPING_METHOD);
		
		let shippingCost = 0;
		for(const smLocation of shippingMethodStep?.shippingMethods || []) {
			shippingCost += smLocation.shippingMethod?.cost ?? 0;
		}
		return shippingCost;
	}

	get isStoreCreditApplied(): boolean {
		const paymentMethodStep = this.getStepData(CHECKOUT_KEY_PAYMENT_METHOD);
		return paymentMethodStep?.applyStoreCredit ?? false;
	}

	get appliedStoreCredit(): number {
		const paymentMethodStep = this.getStepData(CHECKOUT_KEY_PAYMENT_METHOD);
		return paymentMethodStep?.availableStoreCredit ?? 0;
	}

	get shippingAddress(): Address | null {
		const shippingAddressStep = this.getStepData(CHECKOUT_KEY_SHIPPING_ADDRESS);
		return shippingAddressStep?.shippingAddress ?? null;
	}

	get shippingMethods(): CheckoutShippingMethodLocation[] {
		const shippingMethodStep = this.getStepData(CHECKOUT_KEY_SHIPPING_METHOD);
		return shippingMethodStep?.shippingMethods ?? [];
	}
}